import {
  Component,
  computed,
  effect,
  HostBinding,
  inject,
  signal,
} from '@angular/core';
import { IconsComponent } from '@shared/components/icons/icon.component';
import {
  bootstrapShieldFillX,
  bootstrapXCircle,
  bootstrapXCircleFill,
} from '@ng-icons/bootstrap-icons';
import {
  CustomButtonComponent,
  CustomModalComponent,
} from '@shared/components';
import {
  buttonConfig,
  customComponentListener,
  ResponseError,
} from '@core/types/types';
import { TranslateModule } from '@ngx-translate/core';
import {
  ModalService,
  RouteWatcherService,
  ToastService,
} from '@core/services';
import { ClaimService } from '@core/services/http/claim.service';
import { Subscription } from 'rxjs';
import { VoidClaimModalContentComponent } from './components/void-claim-modal-content/void-claim-modal-content.component';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@env/environment';
import { claimStatus } from '@core/enums/enums';

@Component({
  selector: 'void-claim-button-component',
  templateUrl: 'void-claim-button.component.html',
  imports: [CustomButtonComponent, TranslateModule],
})
export class VoidClaimButtonComponent {
  @HostBinding('style.display') display = 'none';

  private readonly claimService = inject(ClaimService);

  private claim$?: Subscription;

  private readonly signals = {
    voidReason: signal<string | undefined>(undefined),
    claim: this.claimService.claim,
  };

  private readonly modalRefs = {
    voidClaim: 'voidClaim',
    claimVoidingProgress: 'claimVoidingProgress',
  };

  protected blockClaimVoidingProgressModalDismiss = true;

  protected configs = computed(
    () =>
      ({
        type: 'button',
        label: {
          key: 'app.pageId.void-claim.title',
        },
        startContent: {
          content: IconsComponent,
          data: {
            icon: bootstrapXCircleFill,
            size: 17,
          },
        },
        endContent: {
          content: IconsComponent,
          data: {
            icon: bootstrapXCircleFill,
            size: 17,
          },
        },
        color: 'tertiary',
        animation: 'slide-in-right',
        action: this.openVoidClaimModal.bind(this),
      } as buttonConfig)
  );

  constructor(
    private readonly routeWatcher: RouteWatcherService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
    private readonly route: ActivatedRoute
  ) {
    effect(() => {
      const claim = this.signals.claim();

      const watchedRoutes = this.routeWatcher.getWatchedRoutes([
        'consult-claim',
      ]);

      if (claim != undefined) {
        this.display =
          watchedRoutes.includes('consult-claim') &&
          ![claimStatus.VOIDED, claimStatus.SETTLED].includes(this.signals.claim()!.status as any)
            ? ''
            : 'none';
      }
    });
  }

  private readonly voidClaimModalActions = computed(() => [
    {
      type: 'button',
      label: {
        key: 'app.pageId.void-claim.cancel',
      },
      color: 'tertiary',
      theme: 'light',
      action: () => {
        this.modalService.dismiss(this.modalRefs.voidClaim);
      },
    },
    {
      type: 'button',
      label: {
        key: 'app.pageId.void-claim.confirm',
      },
      disabled: this.signals.voidReason() == undefined,
      color: 'primary',
      action: this.voidClaim.bind(this),
    },
  ]);

  openVoidClaimModal() {
    const modalConfig = () => ({
      type: 'custom',
      title: 'app.pageId.void-claim.title',
      size: 'md',
      body: {
        content: VoidClaimModalContentComponent,
        data: {
          listeners: [
            {
              event: 'onVoidClaimReasonInputChange',
              action: this.onVoidClaimReasonInputChange.bind(this),
            },
          ] as customComponentListener[],
        },
      },
      actions: this.voidClaimModalActions(),
    });

    this.modalService.open(
      CustomModalComponent,
      modalConfig as any,
      this.modalRefs.voidClaim
    );
  }

  beforeModalDismiss() {
    return !this.blockClaimVoidingProgressModalDismiss;
  }

  onVoidClaimReasonInputChange(data: { reason: string | undefined }) {
    this.signals.voidReason.set(data.reason);
  }

  voidClaim() {
    this.modalService.dismiss(this.modalRefs.voidClaim);
    this.openProgressModal();

    const token =
      this.claimService.initialClaimInfo()?.token ||
      this.route.snapshot.queryParamMap.get('token')!;

    const providerSlug =
      this.claimService.initialClaimInfo()?.providerSlug ||
      this.route.snapshot.queryParamMap.get('providerSlug')!;

    this.claim$ = this.claimService
      .voidClaim(
        providerSlug,
        this.signals.claim()!.id,
        this.signals.voidReason()!,
        token
      )
      .subscribe((response) => {
        if (response.statusCode != 200) {
          this.blockClaimVoidingProgressModalDismiss = false;

          this.modalService.dismiss(this.modalRefs.claimVoidingProgress);
          
          this.toastService.show({
            ref: 'claimVoidingError',
            color: 'tertiary',
            message: {
              key: 'error.generic',
              params: {
                message: (response as any as ResponseError).error.message,
                code: (response as any as ResponseError).error.error,
              },
            },
            icon: bootstrapShieldFillX,
          });

          throw response;
        }

        this.loadClaimInfo(this.signals.claim()!.id);
      });
  }

  openProgressModal() {
    const modalConfig = () => ({
      type: 'progress',
      beforeDismiss: this.beforeModalDismiss.bind(this),
      title: `app.pageId.voiding-claim`,
      message: 'app.pageId.void-claim.voiding-claim',
      size: 'sm',
      loader: {
        type: 'spinner',
        color: 'tertiary',
      },
    });

    this.modalService.open(
      CustomModalComponent,
      modalConfig as any,
      this.modalRefs.claimVoidingProgress
    );
  }

  loadClaimInfo(solicitudReclamoId: number) {
    const token =
      this.claimService.initialClaimInfo()?.token ||
      this.route.snapshot.queryParamMap.get('token')!;

    const providerSlug =
      this.claimService.initialClaimInfo()?.providerSlug ||
      this.route.snapshot.queryParamMap.get('providerSlug')!;

    this.claimService
      .getCreatedClaimInfo(
        providerSlug,
        solicitudReclamoId,
        token
      )
      .subscribe(
        (response) => {
          if (response.statusCode === 202) {
            setTimeout(() => {
              this.loadClaimInfo(solicitudReclamoId);
            }, environment.pollingInterval);
          } else {
            this.blockClaimVoidingProgressModalDismiss = false;

            this.modalService.dismiss(
              this.modalRefs.claimVoidingProgress
            );

            this.toastService.show({
              ref: 'claimVoided',
              color: 'success',
              message: { key: 'app.pageId.claim-voided' },
              icon: bootstrapShieldFillX,
            });

            this.claimService.setClaim(response.data);
          }
        },
        (err: ResponseError) => {
          this.blockClaimVoidingProgressModalDismiss = false;

          this.modalService.dismiss(
            this.modalRefs.claimVoidingProgress
          );

          this.toastService.show({
            ref: 'claimVoidedError',
            color: 'tertiary',
            message: {
              key: 'error.generic',
              params: { message: err.error.message, code: err.error.error },
            },
            icon: bootstrapXCircle,
          });
        }
      );
  }


  ngOnDestroy(): void {
    this.claim$?.unsubscribe();
  }
}
